import PropTypes from 'prop-types';
import React, { useRef, useEffect, useCallback } from 'react';
import ErrorBoundary from '../../errorBoundary/errorBoundary';
import PanelContext from './panelContext';
import PanelFallbackView from './panelFallbackView';
import PanelPropTypes from './panelPropTypes';
import panelTypeConfig from './panelTypeConfig';
import { trackAdobeFeature } from '../../../utils/trackers/adobe';
import { PANEL_CLOSE_BUTTON_CLASS } from '../../../constants';

const hidePanelContentsClass = 'sidebar-panel-hide-contents';

const transformTranslateX = translateX => ({ transform: `translateX(${translateX})` });

function updatePosition(panelRef, initialPosition, targetPosition, hideContents) {
  if (initialPosition !== targetPosition) {
    setTimeout(() => {
      // if(panelRef.current){
      // }
      panelRef.current.classList.remove(hidePanelContentsClass);

      const targetStyle = transformTranslateX(targetPosition);
      panelRef.current.style.transform = targetStyle.transform;

      if (hideContents) {
        const transitionEndListener = () => {
          panelRef.current.classList.add(hidePanelContentsClass);
          panelRef.current.removeEventListener('transitionend', transitionEndListener);
        };

        panelRef.current.addEventListener('transitionend', transitionEndListener);
      }
    }, 0);
  }
}

function updateFocus(panelRef, hasFocus) {
  if (hasFocus) {
    const closeButton = panelRef.current.querySelector(`.${PANEL_CLOSE_BUTTON_CLASS}`);
    if (closeButton) {
      setTimeout(() => {
        closeButton.focus();
      });
    }
  }
}

export default function Panel({
  panel,
  initialPosition,
  targetPosition,
  hasFocus,
  hideContents,
  isOpen,
  openPanel,
  closePanel,
  closeNextPanel,
}) {
  const {
    component: PanelComponent,
    analytics: { openPanelFeature, closePanelFeature },
  } = panelTypeConfig[panel.type];

  const panelRef = useRef(null);

  useEffect(() => {
    updatePosition(panelRef, initialPosition, targetPosition, hideContents);
  }, [panelRef, initialPosition, targetPosition, hideContents]);

  useEffect(() => {
    updateFocus(panelRef, hasFocus);
  }, [panelRef, hasFocus]);

  const trackFeature = useCallback((feature) => {
    if (isOpen) {
      trackAdobeFeature(feature);
    }
  }, [isOpen]);

  useEffect(() => {
    // This is only being emitted for isOpen=true because we are not actually observing the
    // isOpen prop, we are replacing this entire component when the isOpen changes.
    // A component is mounted/unmounted with isOpen=true;
    // a completely separate component is mounted/unmounted with isOpen=false.
    //
    // isOpen=false is being used to create a slide effect from left to right.
    trackFeature(openPanelFeature);
    return () => {
      trackFeature(closePanelFeature);
    };
  }, [closePanelFeature, isOpen, openPanelFeature, trackFeature]);

  const panelContext = {
    closePanel: useCallback(
      () => closePanel(panel),
      [closePanel, panel],
    ),
    closeNextPanel: useCallback(
      () => closeNextPanel(panel),
      [closeNextPanel, panel],
    ),
    openPanel: useCallback(
      ({
        component, launchProps, openerId, index = panel.index + 1,
      }) => openPanel(panel.group, component, {
        launchProps,
        index,
        openerId,
      }), [openPanel, panel],
    ),
    getId: useCallback(() => `${panel.type}-${panel.index}`, [panel]),
    didOpenPanel: (id) => {
      if (!panel.openerId) return false;
      return panel.openerId === id;
    },
  };

  return (
    <div className="sidebar-panel" style={transformTranslateX(initialPosition)} ref={panelRef}>
      <PanelContext.Provider value={panelContext}>
        <ErrorBoundary renderFallbackView={() => (<PanelFallbackView />)}>
          <PanelComponent panel={panel} />
        </ErrorBoundary>
      </PanelContext.Provider>
    </div>
  );
}

Panel.propTypes = {
  panel: PropTypes.shape(PanelPropTypes).isRequired,
  initialPosition: PropTypes.string.isRequired,
  targetPosition: PropTypes.string.isRequired,
  hasFocus: PropTypes.bool,
  hideContents: PropTypes.bool,
  isOpen: PropTypes.bool,
  openPanel: PropTypes.func.isRequired,
  closePanel: PropTypes.func.isRequired,
  closeNextPanel: PropTypes.func.isRequired,
};

Panel.defaultProps = {
  hasFocus: false,
  hideContents: false,
  isOpen: false,
};
